import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useGameStore } from '../../core/store';
import {
  EXIT_CHECKPOINT_Z,
  TUNNEL_BACK_Z,
  TUNNEL_EXIT_Z,
  TUNNEL_MILESTONES_EARLY,
  TUNNEL_MILESTONES_LATE,
  TUNNEL_START_Z,
} from '../../data/tunnel';
import { startTunnelBed, stopTunnelBed, preloadSkillUnlock } from '../audio';
import { TunnelWalker } from './TunnelWalker';
import { TunnelBoards } from './TunnelBoards';
import { JerseyMesh } from './Jersey';
import { TunnelExitPortal, stadiumRevealFromZ } from './TunnelExitPortal';
import { TunnelExitCinematic } from './TunnelExitCinematic';
import { ExitCheckpoint } from './ExitCheckpoint';
import { useTunnelExitStore } from './tunnelExitStore';
import { createTunnelConcreteTexture } from './tunnelExitStadiumTextures';

/** Semi-dark stadium tunnel — dark charcoal slate concrete with dynamic lighting reveals */
const WALL = '#282c34';
const LED = '#f2f6ff';
const FLOOR = '#4a515c';

function MilestoneType({
  label,
  z,
  wall,
}: {
  label: string;
  z: number;
  wall: 'left' | 'right';
}) {
  const x = wall === 'left' ? -2.48 : 2.48;
  const rotY = wall === 'left' ? Math.PI / 2 : -Math.PI / 2;
  const tex = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, 512, 64);
    ctx.fillStyle = 'rgba(220,230,245,0.45)';
    ctx.font = 'bold 36px Bebas Neue, Impact, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, 256, 32);
    const t = new THREE.CanvasTexture(canvas);
    t.colorSpace = THREE.SRGBColorSpace;
    t.flipY = true;
    return t;
  }, [label]);

  return (
    <mesh position={[x, 2.75, z]} rotation={[0, rotY, 0]}>
      <planeGeometry args={[2.2, 0.28]} />
      <meshBasicMaterial
        map={tex}
        transparent
        opacity={0.5}
        side={THREE.DoubleSide}
        toneMapped={false}
      />
    </mesh>
  );
}

/**
 * Dynamic runway and wall lights — 5 cleanly spaced floor lamp pairs and
 * smooth, even wall wash illumination across the entire corridor.
 */
function RunwayLights({ runnerZ }: { runnerZ: number }) {
  const kickMats = useRef<(THREE.MeshBasicMaterial | null)[]>([]);
  const scrollGroup = useRef<THREE.Group>(null);

  // 5 cleanly spaced floor lamp pairs along the corridor bays (5.6m spacing)
  const floorKickZ = useMemo(() => [4.1, 9.7, 15.3, 20.9, 26.5], []);

  // Ceiling LED bars aligned with bays
  const ceilingZ = useMemo(
    () => [2.7, 5.5, 8.3, 11.1, 13.9, 16.7, 19.5, 22.3, 25.1, 27.9],
    [],
  );

  useFrame(() => {
    const scroll = useTunnelExitStore.getState().envScroll;
    if (scrollGroup.current) {
      scrollGroup.current.position.z = scroll * 1.55;
    }
    for (let i = 0; i < floorKickZ.length; i++) {
      const z = floorKickZ[i];
      const near = runnerZ + 8 > z;
      const m = kickMats.current[i];
      if (m) m.opacity = near ? 1 : 0.65;
    }
  });

  return (
    <group>
      <group ref={scrollGroup}>
        {/* Ceiling LED bars */}
        {ceilingZ.map((z) => {
          const on = runnerZ + 9 > z;
          return (
            <group key={`ceil-${z}`} position={[0, 3.98, z]}>
              <mesh>
                <boxGeometry args={[2.8, 0.04, 0.12]} />
                <meshBasicMaterial
                  color={LED}
                  transparent
                  opacity={on ? 1 : 0.6}
                  toneMapped={false}
                />
              </mesh>
            </group>
          );
        })}

        {/* 5 cleanly spaced floor kick lamp pairs */}
        {floorKickZ.map((z, i) => {
          const on = runnerZ + 8 > z;
          return (
            <group key={`kick-${z}`}>
              {([-2.38, 2.38] as const).map((x) => (
                <group key={x} position={[x, 0.04, z]}>
                  {/* Outer dark beveled lamp housing */}
                  <mesh position={[0, 0.02, 0]}>
                    <boxGeometry args={[0.36, 0.06, 0.16]} />
                    <meshStandardMaterial color="#20242c" roughness={0.6} metalness={0.6} />
                  </mesh>

                  {/* Frosted glowing diffuser pane emitting soft light */}
                  <mesh position={[0, 0.05, 0]}>
                    <boxGeometry args={[0.28, 0.03, 0.1]} />
                    <meshBasicMaterial
                      ref={
                        x < 0
                          ? (el) => {
                              kickMats.current[i] = el;
                            }
                          : undefined
                      }
                      color="#eaf4ff"
                      transparent
                      opacity={on ? 1 : 0.65}
                      toneMapped={false}
                    />
                  </mesh>

                  <pointLight
                    position={[x < 0 ? 0.18 : -0.18, 0.28, 0]}
                    intensity={on ? 1.15 : 0.55}
                    distance={4.4}
                    decay={2}
                    color="#e8f0fc"
                  />
                </group>
              ))}
            </group>
          );
        })}
      </group>

      {/* Smooth, even wall-wash fill lights spanning the corridor */}
      <pointLight
        position={[0, 2.8, 6]}
        intensity={3.2}
        distance={24}
        color="#e4edf8"
        decay={2}
      />
      <pointLight
        position={[0, 2.8, 15]}
        intensity={3.4}
        distance={26}
        color="#e6f0fa"
        decay={2}
      />
      <pointLight
        position={[0, 2.8, 24]}
        intensity={3.6}
        distance={26}
        color="#e8f2fc"
        decay={2}
      />

      {/* Dynamic player torch / ambient reveal light that moves with player */}
      <pointLight
        position={[0, 2.4, runnerZ + 1.2]}
        intensity={3.2}
        distance={14}
        color="#e8f2fc"
        decay={2}
      />
    </group>
  );
}

/** Dark charcoal slate concrete shell — mouth frame stays put; tunnel body scrolls back on exit. */
function TunnelShell() {
  const scrollGroup = useRef<THREE.Group>(null);
  const panel = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: WALL,
        roughness: 0.82,
        metalness: 0.1,
      }),
    [],
  );

  const wallEnd = TUNNEL_EXIT_Z - 8.2;
  const wallStart = TUNNEL_BACK_Z - 14;
  const length = wallEnd - wallStart;
  const mid = wallStart + length / 2;
  const wallX = 2.68;

  const floorMap = useMemo(() => {
    const tex = createTunnelConcreteTexture('floor');
    tex.repeat.set(1, Math.max(6, length / 3.4));
    tex.anisotropy = 8;
    return tex;
  }, [length]);

  useFrame(() => {
    if (!scrollGroup.current) return;
    scrollGroup.current.position.z = useTunnelExitStore.getState().envScroll;
  });

  return (
    <group>
      {/* Floor */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0, mid]}
        receiveShadow
      >
        <planeGeometry args={[5.2, length]} />
        <meshStandardMaterial
          map={floorMap}
          color={FLOOR}
          roughness={0.62}
          metalness={0.18}
        />
      </mesh>
      {([-2.46, 2.46] as const).map((x) => (
        <mesh key={x} position={[x, 0.035, mid]}>
          <boxGeometry args={[0.22, 0.07, length]} />
          <meshStandardMaterial color="#6a7380" roughness={0.55} metalness={0.22} />
        </mesh>
      ))}

      {/* Scrolling tunnel body */}
      <group ref={scrollGroup}>
        <mesh position={[-wallX, 2.05, mid]} material={panel}>
          <boxGeometry args={[0.55, 4.12, length]} />
        </mesh>
        <mesh position={[wallX, 2.05, mid]} material={panel}>
          <boxGeometry args={[0.55, 4.12, length]} />
        </mesh>

        <mesh position={[0, 4.08, mid]} material={panel}>
          <boxGeometry args={[5.0, 0.22, length]} />
        </mesh>
      </group>

      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0, TUNNEL_EXIT_Z - 4]}
      >
        <planeGeometry args={[5.2, 8.4]} />
        <meshStandardMaterial
          map={floorMap}
          color={FLOOR}
          roughness={0.62}
          metalness={0.18}
        />
      </mesh>
    </group>
  );
}

function ProgressiveLights({ reveal }: { reveal: number }) {
  const beat = useTunnelExitStore((s) => s.beat);
  const exitCinematic = beat === 'exit' || beat === 'fade';
  const exitMul = exitCinematic ? 1 : reveal;
  const fogNear = exitCinematic ? 90 : THREE.MathUtils.lerp(10, 26, reveal);
  const fogFar = exitCinematic ? 280 : THREE.MathUtils.lerp(34, 62, reveal);
  const fogCol = exitCinematic ? '#070a10' : '#0c0e12';

  return (
    <>
      <ambientLight intensity={0.76} color="#b8ccdf" />
      <hemisphereLight args={['#e4effa', '#242a34', 0.85]} />
      <pointLight
        position={[0, 2.8, 2]}
        intensity={2.8}
        color="#e8f0fc"
        distance={26}
        decay={2}
      />
      {/* Stadium floodlight glow spilling into the tunnel — only active past 5th card */}
      {exitMul > 0.01 && (
        <pointLight
          position={[0, 2.6, TUNNEL_EXIT_Z - 2]}
          intensity={3.8 * exitMul}
          color="#ffe6b0"
          distance={22}
          decay={2}
        />
      )}
      <fog attach="fog" args={[fogCol, fogNear, fogFar]} />
      <color attach="background" args={[exitCinematic ? '#05070b' : '#101318']} />
    </>
  );
}

function PreviewCamera() {
  useFrame(({ camera }) => {
    camera.up.set(0, 1, 0);
    camera.position.set(0, 1.55, TUNNEL_START_Z + 0.2);
    camera.lookAt(0, 1.45, 14);
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.fov = 50;
      camera.updateProjectionMatrix();
    }
  });
  return null;
}

export function TunnelScene() {
  const walking = useGameStore((s) => s.tunnelWalking);
  const runnerZ = useGameStore((s) => s.runnerZ);
  const exitBeat = useTunnelExitStore((s) => s.beat);

  useEffect(() => {
    void startTunnelBed();
    void preloadSkillUnlock();
    return () => stopTunnelBed(0.6);
  }, []);

  const progress = walking
    ? Math.min(1, Math.max(0, runnerZ / TUNNEL_EXIT_Z))
    : 0.15;

  const exitProgress = exitBeat === 'fp' ? progress : Math.max(progress, 0.92);

  const reveal = stadiumRevealFromZ(
    walking ? runnerZ : 0,
    exitBeat === 'exit' || exitBeat === 'fade',
  );

  const panelFade = THREE.MathUtils.clamp(
    1 - (exitProgress - 0.9) / 0.1,
    0,
    1,
  );

  return (
    <>
      {walking ? <TunnelWalker /> : <PreviewCamera />}
      <ProgressiveLights reveal={reveal} />
      <TunnelShell />
      <RunwayLights runnerZ={walking ? runnerZ : 2} />
      <TunnelExitPortal reveal={reveal} />
      <ExitCheckpoint runnerZ={walking ? runnerZ : EXIT_CHECKPOINT_Z} />
      {/* Jersey-back exit shot removed — mouth hits black + VO via scheduleExitHandoff */}
      <TunnelExitCinematic active={false} prewarm={walking} />

      <TunnelBoards fade={panelFade} />

      {TUNNEL_MILESTONES_EARLY.map((label, i) => (
        <MilestoneType
          key={label}
          label={label}
          z={6.2 + i * 2.9}
          wall={i % 2 === 0 ? 'right' : 'left'}
        />
      ))}
      {TUNNEL_MILESTONES_LATE.map((label, i) => {
        const z = 31 + i * 1.85;
        if (z > TUNNEL_EXIT_Z - 10) return null;
        return (
          <MilestoneType
            key={label}
            label={label}
            z={z}
            wall={i % 2 === 0 ? 'left' : 'right'}
          />
        );
      })}

      {!walking && <JerseyMesh position={[0, 0.55, TUNNEL_START_Z - 0.6]} />}

      {!walking && (
        <spotLight
          position={[0.3, 3.5, 1]}
          angle={0.4}
          penumbra={0.7}
          intensity={28}
          color="#e8eeff"
          castShadow
        />
      )}
    </>
  );
}
